// ============================================================================
// folder-audio-receipt.ts — what happened to each folder recording
// ----------------------------------------------------------------------------
// A Stage 1 drop transcribes an audio file into Inspector Notes and keeps no
// audio row. A folder import (Import Projects, the Quote Run) does the same
// later, in the run (project-audio-notes-preparation.ts), from bytes kept in
// the project's video_blobs store. With no row, this record on the form is
// the only durable trace: it keeps each recording from being added twice and
// names the ones set aside. formData is saved and read back with the project,
// so it survives the saves a new project field would not.
// ============================================================================

/** The video_blobs `kind` of a folder recording waiting for its run. */
export const FOLDER_AUDIO_BLOB_KIND = 'folder-audio';
/** Stage 1's limit on a recording (it bounds the decode in memory too). */
export const AUDIO_FILE_MAX_BYTES = 20 * 1024 * 1024;
/** The formData key that holds the record. */
export const FOLDER_AUDIO_RECEIPT_KEY = 'stage1FolderAudio';

export interface FolderAudioReceiptEntry {
    status: 'appended' | 'set-aside';
    descriptor: string;
    name: string;
    relativePath?: string;
    at: string;
    reason?: string;
    /** A set-aside the service caused; a later run may try the recording again. */
    retry?: boolean;
}

export interface FolderAudioReceipt {
    version: 1;
    sources: Record<string, FolderAudioReceiptEntry>;
}

type Form = Record<string, unknown> | null | undefined;

/** Same recording: the same source file id with the same size and date. */
export function folderAudioDescriptor(source: { size?: unknown; lastModified?: unknown }): string {
    return `${Number(source?.size) || 0}:${Number(source?.lastModified) || 0}`;
}

export function folderAudioReceipt(form: Form): FolderAudioReceipt {
    const value = form?.[FOLDER_AUDIO_RECEIPT_KEY] as FolderAudioReceipt | undefined;
    return value?.version === 1 && value.sources && typeof value.sources === 'object'
        ? value
        : { version: 1, sources: {} };
}

export function withFolderAudioReceiptEntry<F extends Record<string, unknown>>(form: F, id: string, entry: FolderAudioReceiptEntry): F {
    const receipt = folderAudioReceipt(form);
    return { ...form, [FOLDER_AUDIO_RECEIPT_KEY]: { version: 1, sources: { ...receipt.sources, [id]: entry } } };
}

/** The recordings a run set aside, shaped like the files readiness scopes and lists. */
export function folderAudioSetAside(project: { formData?: Form } | null | undefined): Array<{ id: string; name: string; relativePath?: string; reason: string }> {
    return Object.entries(folderAudioReceipt(project?.formData).sources)
        .filter(([, entry]) => entry?.status === 'set-aside')
        .map(([id, entry]) => ({
            id,
            name: entry.name || id,
            ...(entry.relativePath ? { relativePath: entry.relativePath } : {}),
            reason: entry.reason || 'The recording could not be transcribed, so it was set aside.',
        }));
}
